import { transparentize } from "polished";
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import { Panel } from "./Panel";

const Container = styled(animated.div)<SpaceProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background-color: ${(props) =>
    transparentize(0.8, props.theme.colors.background)};
  backdrop-filter: blur(6px);
  z-index: 5000;
  padding: 12px;
  overflow-y: auto;
  ${space};
`;

const KillPlane = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const AniPanel = styled(animated(Panel))`
  margin: auto;
  z-index: 1;
`;

export type ModalProps = {
  children: React.ReactNode;
  fullscreenOnMobile?: boolean;
  onClose?: () => void;
} & React.ComponentProps<typeof Panel>;

function ModalPortal({ children }: { children: React.ReactNode }) {
  const modalEl = useRef<HTMLDivElement>(document.createElement("div"));

  useEffect(() => {
    const root = document.getElementById("root");
    const el = modalEl.current;
    root.parentNode.appendChild(el);

    return () => {
      root.parentNode.removeChild(el);
    };
  }, []);

  return ReactDOM.createPortal(children, modalEl.current);
}

export function Modal({
  children,
  onClose,
  style,
  fullscreenOnMobile,
  ...rest
}: ModalProps) {
  const containerSpring = useSpring({
    from: {
      backdropFilter: "blur(0px)",
    },
    backdropFilter: "blur(6px)",
  });

  useEffect(() => {
    const oldOverflow = document.body.style.overflowY;
    document.body.style.overflowY = "hidden";

    return () => {
      document.body.style.overflowY = oldOverflow;
    };
  }, []);

  const spring = useSpring({
    from: {
      opacity: 0,
      y: 100,
    },
    opacity: 1,
    y: 0,
  });

  return (
    <ModalPortal>
      <Container style={containerSpring} p={[fullscreenOnMobile ? 0 : 3, 3]}>
        <KillPlane onClick={onClose} />
        <AniPanel
          style={{
            ...spring,
            ...(style ?? {}),
          }}
          {...rest}
        >
          {children}
        </AniPanel>
      </Container>
    </ModalPortal>
  );
}

export default Modal;
